// Barra de navegação superior
import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { User, LogIn, UserPlus, Menu, X } from 'lucide-react';
import AuthModal from './AuthModal';
import UserProfile from './UserProfile';

function Navbar({ onLogoClick }) {
  const { user, isAuthenticated, loading } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLoginClick = () => {
    setAuthModalMode('login');
    setShowAuthModal(true);
    setIsMobileMenuOpen(false);
  };

  const handleRegisterClick = () => {
    setAuthModalMode('register');
    setShowAuthModal(true);
    setIsMobileMenuOpen(false);
  };

  const handleUserClick = () => {
    setShowUserProfile(true);
    setIsMobileMenuOpen(false);
  };

  const handleCloseAuthModal = () => {
    setShowAuthModal(false);
  };

  const handleCloseUserProfile = () => {
    setShowUserProfile(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex-shrink-0">
              {isAuthenticated && (
                <button 
                  onClick={onLogoClick}
                  className="text-xl font-bold text-blue-600 hover:text-blue-700 transition-colors cursor-pointer"
                >
                  Dash Simples
                </button>
              )}
            </div>

            {/* Menu Desktop */}
            <div className="hidden md:flex items-center space-x-4">
              {loading ? (
                <div className="w-20 h-8 bg-gray-200 animate-pulse rounded"></div>
              ) : isAuthenticated ? (
                <div className="flex items-center space-x-3">
                  <span className="text-gray-700 text-sm">
                    Olá, {user?.name || user?.email?.split('@')[0] || 'Usuário'}
                  </span>
                  <button
                    onClick={handleUserClick}
                    className="flex items-center space-x-2 bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-2 rounded-lg transition-colors duration-200"
                  >
                    <User className="w-4 h-4" />
                    <span className="text-sm font-medium">Perfil</span>
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleLoginClick}
                    className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 px-3 py-2 rounded-lg transition-colors duration-200"
                  >
                    <LogIn className="w-4 h-4" />
                    <span className="text-sm font-medium">Entrar</span>
                  </button>
                  <button
                    onClick={handleRegisterClick}
                    className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span className="text-sm font-medium">Criar Conta</span>
                  </button>
                </div>
              )}
            </div>

            {/* Menu Mobile Button */}
            <div className="md:hidden">
              <button
                onClick={toggleMobileMenu}
                className="text-gray-700 hover:text-blue-600 p-2"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>

          {/* Menu Mobile */}
          {isMobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 py-4">
              {loading ? (
                <div className="px-4">
                  <div className="w-full h-10 bg-gray-200 animate-pulse rounded"></div>
                </div>
              ) : isAuthenticated ? (
                <div className="px-4 space-y-3">
                  <div className="text-gray-700 text-sm pb-2 border-b border-gray-200">
                    Olá, {user?.name || user?.email?.split('@')[0] || 'Usuário'}
                  </div>
                  <button
                    onClick={handleUserClick}
                    className="flex items-center space-x-2 text-blue-600 hover:bg-blue-50 w-full text-left px-3 py-2 rounded-lg transition-colors duration-200"
                  >
                    <User className="w-4 h-4" />
                    <span className="text-sm font-medium">Ver Perfil</span>
                  </button>
                </div>
              ) : (
                <div className="px-4 space-y-2">
                  <button
                    onClick={handleLoginClick}
                    className="flex items-center space-x-2 text-gray-700 hover:bg-gray-50 w-full text-left px-3 py-2 rounded-lg transition-colors duration-200"
                  >
                    <LogIn className="w-4 h-4" />
                    <span className="text-sm font-medium">Entrar</span>
                  </button>
                  <button
                    onClick={handleRegisterClick}
                    className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white w-full text-center px-4 py-2 rounded-lg transition-colors duration-200"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span className="text-sm font-medium">Criar Conta</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Modal de Autenticação */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={handleCloseAuthModal}
        initialMode={authModalMode}
      />

      {/* Modal de Perfil do Usuário */}
      {showUserProfile && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={handleCloseUserProfile}
          />
          
          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              {/* Botão de fechar */}
              <button
                onClick={handleCloseUserProfile}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors z-10"
                aria-label="Fechar"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Conteúdo */}
              <div className="p-6">
                <UserProfile onClose={handleCloseUserProfile} />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;
